/* MP3-based narration with Promise-driven completion.
 *
 * The phase loop awaits play() — so a caption never disappears until the
 * narration that goes with it is FINISHED. No more text-out-paces-voice.
 *
 * Audio files live in /public/demo/audio/{phase}-{lang}.mp3 and are
 * pre-generated by `scripts/generate-demo-audio.mjs` using OpenAI TTS-1-HD.
 *
 * If a file is missing (script not run yet) play() rejects with a
 * distinguishable 'missing' error so the orchestrator can fall back to
 * browser TTS gracefully.
 */

import type { ScreenKey } from './phases';
import type { Lang } from './voice';

export class AudioMissing extends Error {
  constructor(url: string) {
    super(`audio file missing: ${url}`);
  }
}

export class DemoAudio {
  private el: HTMLAudioElement | null = null;
  private muted = false;
  private paused = false;

  setMuted(m: boolean): void {
    this.muted = m;
    if (this.el) this.el.muted = m;
  }

  isMuted(): boolean { return this.muted; }

  /** Resolve when the clip ends. Reject with AudioMissing if the MP3
   *  doesn't exist. Reject with other errors if playback fails. */
  play(phase: ScreenKey, lang: Lang): Promise<void> {
    this.stop();
    return new Promise<void>((resolve, reject) => {
      const url = `/demo/audio/${phase}-${lang}.mp3`;
      const el = new Audio(url);
      el.preload = 'auto';
      el.muted = this.muted;
      el.volume = 1.0;
      this.el = el;

      let resolved = false;
      const finish = (err?: Error) => {
        if (resolved) return;
        resolved = true;
        if (this.el === el) this.el = null;
        if (err) reject(err); else resolve();
      };

      el.addEventListener('ended', () => finish());
      el.addEventListener('error', () => {
        // Distinguish 404 (file missing) from other errors. Most browsers
        // surface a generic MEDIA_ERR_SRC_NOT_SUPPORTED for missing files,
        // and we can't read the HTTP status cleanly from <audio>, so we
        // probe with HEAD before falling back further.
        fetch(url, { method: 'HEAD' }).then((res) => {
          if (res.status === 404) finish(new AudioMissing(url));
          else finish(new Error(`audio error (${res.status}): ${url}`));
        }).catch(() => finish(new AudioMissing(url)));
      });

      el.play().catch((err) => {
        // Autoplay policy might block — caller will see the rejection.
        // Splash gate guarantees a user gesture; this should only happen
        // on the very first speak() if the splash was bypassed.
        finish(err instanceof Error ? err : new Error(String(err)));
      });
    });
  }

  pause(): void {
    if (!this.el) return;
    this.paused = true;
    this.el.pause();
  }

  resume(): void {
    if (!this.el) return;
    this.paused = false;
    this.el.play().catch(() => {});
  }

  /** Cancel any in-flight playback. Caller's pending promise resolves cleanly. */
  stop(): void {
    if (!this.el) return;
    const el = this.el;
    this.el = null;
    try {
      el.pause();
      el.src = '';
      el.load();
    } catch {}
  }
}

export const demoAudio = new DemoAudio();
